import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useScrollLock } from "../hooks/useScrollLock";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import FullscreenControl from "../components/FullscreenControl";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polygon,
  Popup,
  useMap,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.css";
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function MeterPopup({
  meters,
  m,
  r,
  hasIncident,
  checked,
  checkedCount,
  batchSize,
}) {
  const [idx, setIdx] = useState(0);
  const mt = meters[idx] || {};
  const mChecked = mt.checked !== undefined ? mt.checked : m.checked;
  const mValue =
    mt.reading_value !== undefined ? mt.reading_value : m.reading_value;
  const mPhoto = mt.photo !== undefined ? mt.photo : m.photo;

  const row = (label, value, bold) => (
    <div style={{ display: "flex", gap: 4, fontSize: 12, marginBottom: 3 }}>
      <span style={{ color: "#9a9a93", minWidth: 76, flexShrink: 0 }}>
        {label}
      </span>
      {bold ? (
        <strong style={{ color: "#1a1a18" }}>{value}</strong>
      ) : (
        <span style={{ color: "#1a1a18" }}>{value}</span>
      )}
    </div>
  );

  return (
    <div
      style={{ fontFamily: "Montserrat, sans-serif", fontSize: 13, width: 240 }}
    >
      {/* Header: status + contador */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span
          style={{
            background: hasIncident
              ? "#fef3c7"
              : checked
                ? "#dcfce7"
                : "#f2f2ef",
            color: hasIncident ? "#92400e" : checked ? "#16a34a" : "#6b6b66",
            borderRadius: 6,
            padding: "2px 8px",
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {hasIncident
            ? "⚠ Incidencia"
            : checked
              ? "✓ Todo medido"
              : checkedCount > 0
                ? `${checkedCount}/${batchSize} medidos`
                : "○ Pendiente"}
        </span>
        {batchSize > 1 && (
          <span style={{ fontSize: 11, color: "#9a9a93", fontWeight: 600 }}>
            {idx + 1} / {batchSize}
          </span>
        )}
      </div>

      {/* Medidor actual */}
      <div style={{ marginBottom: 6 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 6,
          }}
        >
          <span
            style={{
              color: mt.has_incident
                ? "#f59e0b"
                : mChecked
                  ? "#16a34a"
                  : "#9a9a93",
              fontSize: 14,
            }}
          >
            {mt.has_incident ? "⚠" : mChecked ? "✓" : "○"}
          </span>
          <strong style={{ fontSize: 14 }}>
            {mt.meter_code || `Medidor ${idx + 1}`}
          </strong>
        </div>

        {mValue && row("Lectura:", mValue, true)}
        {(mt.reader_name || r.employee_name) &&
          row("Auditor:", mt.reader_name || r.employee_name, false)}
        {mt.read_at &&
          row(
            "Fecha lectura:",
            (() => {
              try {
                return new Date(mt.read_at).toLocaleString("es-AR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                });
              } catch {
                return mt.read_at;
              }
            })(),
            false,
          )}

        {mPhoto && (
          <img
            src={mPhoto}
            alt="foto"
            style={{
              width: "100%",
              borderRadius: 6,
              margin: "6px 0 4px",
              cursor: "pointer",
              maxHeight: 130,
              objectFit: "cover",
            }}
            onClick={() => window.open(mPhoto, "_blank")}
          />
        )}

        {r.created_at && (
          <div
            style={{
              borderTop: "1px solid #f0f0ec",
              marginTop: 6,
              paddingTop: 6,
            }}
          >
            {row(
              "Fecha ruta:",
              (() => {
                try {
                  return new Date(r.created_at).toLocaleDateString("es-AR");
                } catch {
                  return r.created_at;
                }
              })(),
              false,
            )}
          </div>
        )}
      </div>

      {/* Navegación */}
      {batchSize > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: "1px solid #f0f0ec",
            paddingTop: 8,
            marginTop: 2,
          }}
        >
          <button
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            style={{
              background: "none",
              border: "1px solid #e6e6e2",
              borderRadius: 6,
              padding: "3px 10px",
              cursor: idx === 0 ? "default" : "pointer",
              color: idx === 0 ? "#c8c8c4" : "#1a1a18",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            ‹ Ant
          </button>
          <div style={{ display: "flex", gap: 3 }}>
            {meters.slice(0, Math.min(batchSize, 8)).map((_, i) => (
              <div
                key={i}
                onClick={() => setIdx(i)}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: i === idx ? "#D4620D" : "#e6e6e2",
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
              />
            ))}
            {batchSize > 8 && (
              <span style={{ fontSize: 10, color: "#9a9a93" }}>…</span>
            )}
          </div>
          <button
            onClick={() => setIdx((i) => Math.min(batchSize - 1, i + 1))}
            disabled={idx === batchSize - 1}
            style={{
              background: "none",
              border: "1px solid #e6e6e2",
              borderRadius: 6,
              padding: "3px 10px",
              cursor: idx === batchSize - 1 ? "default" : "pointer",
              color: idx === batchSize - 1 ? "#c8c8c4" : "#1a1a18",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            Sig ›
          </button>
        </div>
      )}
    </div>
  );
}

function meterDotIcon(color, size = 12) {
  return L.divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.28)"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function clusterIcon(cluster) {
  const n = cluster.getChildCount();
  const size = n < 10 ? 28 : n < 100 ? 34 : 40;
  const fs = n < 10 ? 11 : 13;
  return L.divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:#2563eb;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:${fs}px;border:2px solid #fff;box-shadow:0 2px 8px rgba(37,99,235,0.35);font-family:Montserrat,sans-serif">${n}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

import {
  getRoutes,
  deleteRoute,
  updateRoute,
  getEmployees,
  getTarifas,
  getRouteHistory,
  completeRouteAssignment,
} from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  Trash2,
  User,
  Edit2,
  X,
  MapPin,
  Clock,
  CheckCircle,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const DEFAULT_CENTER = [-34.6037, -58.3816];
const ROUTE_COLORS = [
  "#D4620D",
  "#16a34a",
  "#9333ea",
  "#dc2626",
  "#0891b2",
  "#b45309",
];

function MapController({ mapRef }) {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
  }, [map]);
  return null;
}

export default function RoutesPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === "admin-plus";
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [selectedRouteIds, setSelectedRouteIds] = useState(new Set());
  const [routeSearch, setRouteSearch] = useState("");
  const [employees, setEmployees] = useState([]);
  const [tarifas, setTarifas] = useState(null);
  const [loading, setLoading] = useState(true);
  const pendingCoordsRef = useRef(null);

  const mapRef = useRef(null);
  const hasFlown = useRef(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  // Modal
  const [showModal, setShowModal] = useState(false);
  useScrollLock(showModal || !!historyRoute);
  const [editingRoute, setEditingRoute] = useState(null);
  const [form, setForm] = useState({
    name: "",
    employee_id: "",
    valor_personalizado: "",
    zona: "",
  });
  const [keepProgress, setKeepProgress] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Historial
  const [historyRoute, setHistoryRoute] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    const [routeRes, empRes, tarifasRes] = await Promise.allSettled([
      getRoutes(),
      getEmployees(),
      getTarifas(),
    ]);
    if (routeRes.status === "fulfilled") {
      const loadedRoutes = routeRes.value;
      setRoutes(loadedRoutes);
      // Guardar coords para fitBounds — el useEffect las aplica cuando el mapa esté listo
      if (!hasFlown.current) {
        const allCoords = loadedRoutes.flatMap((r) =>
          (r.meter_codes || [])
            .map((m) => [parseFloat(m.latitude), parseFloat(m.longitude)])
            .filter(([lat, lng]) => !isNaN(lat) && !isNaN(lng)),
        );
        if (allCoords.length > 0) pendingCoordsRef.current = allCoords;
      }
    }
    if (empRes.status === "fulfilled") setEmployees(empRes.value);
    if (tarifasRes.status === "fulfilled") setTarifas(tarifasRes.value);
    setLoading(false);
  }

  // fitBounds se ejecuta después de que routes cambia y MapContainer ya está montado
  useEffect(() => {
    if (!pendingCoordsRef.current || !mapRef.current || hasFlown.current)
      return;
    hasFlown.current = true;
    const coords = pendingCoordsRef.current;
    pendingCoordsRef.current = null;
    mapRef.current.fitBounds(L.latLngBounds(coords), {
      padding: [50, 50],
      maxZoom: 16,
    });
  }, [routes]);

  const firstMeter = routes
    .flatMap((r) => r.meter_codes || [])
    .find((m) => !isNaN(parseFloat(m.latitude)));
  const mapCenter = firstMeter
    ? [parseFloat(firstMeter.latitude), parseFloat(firstMeter.longitude)]
    : DEFAULT_CENTER;

  // ── Modal ─────────────────────────────────────────────────────────────────

  function openEditModal(route) {
    setEditingRoute(route);
    setForm({
      name: route.name,
      employee_id: route.employee_id?.toString() || "",
      valor_personalizado:
        route.valor_personalizado != null
          ? String(route.valor_personalizado)
          : "",
      zona: route.zona || "",
    });
    setKeepProgress(false);
    setError("");
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingRoute(null);
    setError("");
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setError("El nombre es requerido");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const valorPers =
        form.valor_personalizado !== ""
          ? parseFloat(form.valor_personalizado)
          : null;
      const zonaVal = form.zona || null;
      const employeeChanged =
        (form.employee_id || null) !==
        (editingRoute.employee_id?.toString() || null);
      await updateRoute(editingRoute.id, {
        name: form.name.trim(),
        employee_id: form.employee_id || null,
        valor_personalizado: valorPers,
        zona: zonaVal,
        ...(employeeChanged && keepProgress && { keep_progress: true }),
      });
      closeModal();
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function handleDelete(route) {
    setDeleteTarget(route);
  }

  async function openHistory(route) {
    setHistoryRoute(route);
    setHistoryData([]);
    setHistoryError("");
    setHistoryLoading(true);
    try {
      const data = await getRouteHistory(route.id);
      setHistoryData(data);
    } catch (err) {
      setHistoryError(err.message || "Error al cargar historial");
    } finally {
      setHistoryLoading(false);
    }
  }

  async function handleCompleteAssignment() {
    if (!historyRoute) return;
    setCompleting(true);
    setHistoryError("");
    try {
      await completeRouteAssignment(historyRoute.id);
      await load();
      const data = await getRouteHistory(historyRoute.id);
      setHistoryData(data);
      setHistoryRoute((r) => ({
        ...r,
        employee_id: null,
        employee_name: null,
      }));
    } catch (err) {
      setHistoryError(err.message || "Error al completar la asignación");
    } finally {
      setCompleting(false);
    }
  }

  function flyToRoute(route) {
    if (!mapRef.current || !route.polygon?.length) return;
    mapRef.current.flyToBounds(route.polygon, {
      padding: [40, 40],
      maxZoom: 17,
      duration: 0.8,
    });
  }

  const activeEmployees = employees.filter((e) => e.active);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <DeleteConfirmModal
        visible={!!deleteTarget}
        message={`¿Eliminar la ruta "${deleteTarget?.name}"? Esta acción no se puede deshacer.`}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await deleteRoute(deleteTarget.id);
          setDeleteTarget(null);
          load();
        }}
      />
      {/* ── Header ── */}
      <div style={s.header}>
        <h1 style={s.title}>
          Rutas <span style={s.badge}>{routes.length}</span>
        </h1>
      </div>

      {/* ── Main layout ── */}
      <div style={s.layout}>
        {/* MAP — 70% */}
        <div style={s.mapWrap}>
          {/* Chip rutas seleccionadas */}
          {selectedRouteIds.size > 0 && (
            <div
              style={{
                position: "absolute",
                top: 12,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1000,
                background: "#D4620D",
                color: "#fff",
                borderRadius: 20,
                padding: "6px 16px",
                fontSize: 13,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 8,
                boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                whiteSpace: "nowrap",
              }}
            >
              {selectedRouteIds.size === 1
                ? routes.find((r) => selectedRouteIds.has(r.id))?.name
                : `${selectedRouteIds.size} rutas seleccionadas`}
              <button
                onClick={() => setSelectedRouteIds(new Set())}
                style={{
                  background: "none",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                }}
                title="Limpiar selección"
              >
                <X size={14} />
              </button>
            </div>
          )}

          <MapContainer
            center={mapCenter}
            zoom={13}
            style={{ width: "100%", height: "100%" }}
            scrollWheelZoom
          >
            <MapController mapRef={mapRef} />
            <FullscreenControl />
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              attribution="&copy; OpenStreetMap &copy; CARTO"
            />

            {/* Markers de las rutas seleccionadas — sin agrupamiento */}
            <>
              {selectedRouteIds.size === 0 && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000,
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      background: "rgba(255,255,255,0.92)",
                      borderRadius: 14,
                      padding: "18px 28px",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 28, marginBottom: 8 }}>🗺️</div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--text)",
                        marginBottom: 4,
                      }}
                    >
                      Seleccioná una o más rutas
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-2)" }}>
                      Hacé clic en las rutas del panel derecho para ver sus
                      medidores
                    </div>
                  </div>
                </div>
              )}
              {routes
                .filter((r) => selectedRouteIds.has(r.id))
                .map((r, ri) => {
                  const routeColor =
                    ROUTE_COLORS[routes.indexOf(r) % ROUTE_COLORS.length];
                  return (r.meter_codes || []).map((m, mi) => {
                    const lat = parseFloat(m.latitude);
                    const lng = parseFloat(m.longitude);
                    if (isNaN(lat) || isNaN(lng)) return null;
                    const meters = m.meters || [];
                    const batchSize = meters.length || 1;
                    const checked = !!m.checked;
                    const hasIncident = meters.some((mt) => !!mt.has_incident);
                    const checkedCount = meters.filter((mt) =>
                      mt.checked !== undefined ? mt.checked : m.checked,
                    ).length;
                    const color = hasIncident
                      ? "#f59e0b"
                      : checked
                        ? "#16a34a"
                        : routeColor;
                    const size = Math.min(20, 12 + (batchSize - 1) * 2);
                    return (
                      <Marker
                        key={`${r.id}-${mi}`}
                        position={[lat, lng]}
                        icon={meterDotIcon(color, size)}
                      >
                        <Popup minWidth={240} maxWidth={260}>
                          <MeterPopup
                            meters={meters}
                            m={m}
                            r={r}
                            hasIncident={hasIncident}
                            checked={checked}
                            checkedCount={checkedCount}
                            batchSize={batchSize}
                          />
                        </Popup>
                      </Marker>
                    );
                  });
                })}
            </>

            {/* Saved route polygons */}
            {routes.map(
              (r, i) =>
                r.polygon?.length >= 3 && (
                  <Polygon
                    key={r.id}
                    positions={r.polygon}
                    pathOptions={{
                      color: ROUTE_COLORS[i % ROUTE_COLORS.length],
                      fillOpacity: 0.13,
                      weight: 2.5,
                    }}
                    eventHandlers={{ click: () => openEditModal(r) }}
                  />
                ),
            )}
          </MapContainer>
        </div>

        {/* SIDEBAR — 30% */}
        <div style={s.sidebar}>
          <div style={s.sidebarHeader}>
            <span style={s.sidebarTitle}>
              {routes.length} ruta{routes.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Buscador */}
          <div style={s.sidebarSearch}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--text-3)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              style={s.sidebarSearchInput}
              placeholder="Buscar ruta..."
              value={routeSearch}
              onChange={(e) => setRouteSearch(e.target.value)}
            />
            {routeSearch && (
              <button
                style={s.sidebarSearchClear}
                onClick={() => setRouteSearch("")}
              >
                ✕
              </button>
            )}
          </div>

          <div style={s.sidebarBody}>
            {loading ? (
              <div style={s.sidebarEmpty}>Cargando...</div>
            ) : routes.length === 0 ? (
              <div style={s.sidebarEmpty}>
                <MapPin
                  size={32}
                  color="#d1d5db"
                  style={{ marginBottom: 10 }}
                />
                <p style={{ color: "var(--text-2)", margin: 0, fontSize: 13 }}>
                  No hay rutas aún.
                  <br />
                  Creá una desde Mapa HD.
                </p>
              </div>
            ) : (
              (() => {
                const filtered = routeSearch.trim()
                  ? routes.filter((r) =>
                      r.name?.toLowerCase().includes(routeSearch.toLowerCase()),
                    )
                  : routes;
                if (filtered.length === 0)
                  return (
                    <div style={s.sidebarEmpty}>
                      <p
                        style={{
                          color: "var(--text-2)",
                          margin: 0,
                          fontSize: 13,
                        }}
                      >
                        Sin resultados para "{routeSearch}"
                      </p>
                    </div>
                  );
                return filtered.map((r, i) => (
                  <div
                    key={r.id}
                    style={{
                      ...s.routeCard,
                      cursor: "pointer",
                      ...(selectedRouteIds.has(r.id)
                        ? {
                            borderLeft: "3px solid #D4620D",
                            background: "var(--primary-bg)",
                          }
                        : {}),
                    }}
                    onClick={() => {
                      const isSelected = selectedRouteIds.has(r.id);
                      setSelectedRouteIds((prev) => {
                        const next = new Set(prev);
                        if (isSelected) next.delete(r.id);
                        else next.add(r.id);
                        return next;
                      });
                      if (!isSelected) flyToRoute(r);
                    }}
                  >
                    <div style={s.routeCardTop}>
                      <div style={s.routeCardLeft}>
                        <span
                          style={{
                            ...s.colorDot,
                            background: ROUTE_COLORS[i % ROUTE_COLORS.length],
                          }}
                        />
                        <span style={s.routeName}>{r.name}</span>
                      </div>
                      <div
                        style={s.routeCardActions}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          style={s.iconBtnGray}
                          title="Historial"
                          onClick={() => openHistory(r)}
                        >
                          <Clock size={12} />
                        </button>
                        {isAdminPlus && (
                          <>
                            <button
                              style={s.iconBtnBlue}
                              title="Editar"
                              onClick={() => openEditModal(r)}
                            >
                              <Edit2 size={12} />
                            </button>
                            <button
                              style={s.iconBtnRed}
                              title="Eliminar"
                              onClick={() => handleDelete(r)}
                            >
                              <Trash2 size={12} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {(() => {
                      const totalMeters = (r.meter_codes || []).reduce(
                        (sum, m) => sum + (m.meters?.length || 0),
                        0,
                      );
                      const precioPorMedidor =
                        parseFloat(
                          tarifas?.precio_por_vehiculo?.[
                            r.employee_vehicle_type
                          ],
                        ) || 0;
                      const zonaPct =
                        r.zona && tarifas?.zonas_porcentaje?.[r.zona]
                          ? parseFloat(tarifas.zonas_porcentaje[r.zona])
                          : 0;
                      const precioFinal =
                        precioPorMedidor * (1 + zonaPct / 100);
                      const valorRef = Math.round(totalMeters * precioFinal);
                      const valorPers =
                        r.valor_personalizado != null
                          ? parseFloat(r.valor_personalizado)
                          : null;

                      const today = new Date().toISOString().split("T")[0];
                      const obj = r.objetivo_completado;
                      const objHoy = obj && obj.fecha === today ? obj : null;

                      return (
                        <div style={s.routeMeta}>
                          <div style={s.routeMetaRow}>
                            <User
                              size={11}
                              color={r.employee_name ? "#D4620D" : "#d8d8d3"}
                            />
                            <span
                              style={r.employee_name ? s.empName : s.empEmpty}
                            >
                              {r.employee_name || "Sin asignar"}
                            </span>
                            {r.zona && (
                              <span style={s.zonaBadge}>
                                Zona {r.zona}
                                {zonaPct > 0 ? ` +${zonaPct}%` : ""}
                              </span>
                            )}
                          </div>
                          <div style={s.routeMetaRow}>
                            <span style={s.routeDate}>
                              {totalMeters} medidor
                              {totalMeters !== 1 ? "es" : ""}
                              {precioPorMedidor > 0 && (
                                <>
                                  {" "}
                                  · Ref:{" "}
                                  <strong style={{ color: "var(--text-2)" }}>
                                    ${valorRef.toLocaleString("es-AR")}
                                  </strong>
                                </>
                              )}
                            </span>
                          </div>
                          {valorPers != null && (
                            <div style={s.routeMetaRow}>
                              <span
                                style={{
                                  ...s.routeDate,
                                  color: "#D4620D",
                                  fontWeight: 700,
                                }}
                              >
                                Valor personalizado: $
                                {valorPers.toLocaleString("es-AR")}
                              </span>
                            </div>
                          )}
                          {objHoy && objHoy.nivel_actual && (
                            <div style={s.routeMetaRow}>
                              <span style={s.objetivoBadge}>
                                🎯 Objetivo {objHoy.nivel_actual.medidores}{" "}
                                medidores
                                {" — "}$
                                {parseFloat(
                                  objHoy.nivel_actual.premio,
                                ).toLocaleString("es-AR")}{" "}
                                extra
                              </span>
                            </div>
                          )}
                          {objHoy &&
                            !objHoy.nivel_actual &&
                            objHoy.medidores_hoy > 0 && (
                              <div style={s.routeMetaRow}>
                                <span
                                  style={{
                                    ...s.routeDate,
                                    color: "var(--text-2)",
                                  }}
                                >
                                  Hoy: {objHoy.medidores_hoy} medidor
                                  {objHoy.medidores_hoy !== 1 ? "es" : ""}
                                </span>
                              </div>
                            )}
                          <div style={s.routeMetaRow}>
                            <span style={s.routeDate}>
                              {r.created_at
                                ? format(new Date(r.created_at), "d MMM yyyy", {
                                    locale: es,
                                  })
                                : "—"}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ));
              })()
            )}
          </div>
        </div>
      </div>

      {/* ── Modal ── */}
      {isAdminPlus && showModal && (
        <div
          style={m.overlay}
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <div style={m.box}>
            <div style={m.header}>
              <h3 style={m.title}>
                {editingRoute ? `Editar ruta` : "Nueva ruta"}
              </h3>
              <button style={m.closeBtn} onClick={closeModal}>
                <X size={16} />
              </button>
            </div>

            {/* Route color + name preview for edit */}
            {editingRoute && (
              <div style={m.editPreview}>
                <span
                  style={{
                    ...s.colorDot,
                    background:
                      ROUTE_COLORS[
                        routes.findIndex((r) => r.id === editingRoute.id) %
                          ROUTE_COLORS.length
                      ],
                  }}
                />
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "var(--text)",
                  }}
                >
                  {editingRoute.name}
                </span>
              </div>
            )}

            {/* Name — only for new routes */}
            {!editingRoute && (
              <div style={m.field}>
                <label style={m.label}>Nombre de la ruta *</label>
                <input
                  style={m.input}
                  autoFocus
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="Ej: Zona Norte Bloque A"
                  onKeyDown={(e) => e.key === "Enter" && handleSave()}
                />
              </div>
            )}

            {/* Zona — siempre visible */}
            <div style={m.field}>
              <label style={m.label}>Zona</label>
              <select
                style={m.input}
                value={form.zona}
                onChange={(e) =>
                  setForm((f) => ({ ...f, zona: e.target.value }))
                }
              >
                <option value="">Sin zona</option>
                {["1", "2", "3", "4"].map((z) => {
                  const pct = parseFloat(tarifas?.zonas_porcentaje?.[z]) || 0;
                  return (
                    <option key={z} value={z}>
                      Zona {z}
                      {pct > 0 ? ` (+${pct}%)` : ""}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Employee */}
            {editingRoute && (
              <div style={m.field}>
                <label style={m.label}>Asignar operario</label>
                <select
                  style={m.input}
                  value={form.employee_id}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, employee_id: e.target.value }));
                    setKeepProgress(false);
                  }}
                >
                  <option value="">Sin asignar</option>
                  {activeEmployees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                      {e.role === "lector-plus" ? " ★" : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Opción mantener progreso — solo cuando se cambia el auditor y hay medidores hechos */}
            {editingRoute &&
              (() => {
                const newEmp = form.employee_id || null;
                const oldEmp = editingRoute.employee_id?.toString() || null;
                const changing = newEmp && newEmp !== oldEmp;
                const hasProgress = (editingRoute.meter_codes || []).some(
                  (mc) => (mc.meters || []).some((mt) => mt.checked),
                );
                if (!changing || !hasProgress) return null;
                return (
                  <div
                    style={{
                      background: "#fffbeb",
                      border: "1px solid #fde68a",
                      borderRadius: 10,
                      padding: "12px 14px",
                      marginBottom: 12,
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={keepProgress}
                        onChange={(e) => setKeepProgress(e.target.checked)}
                        style={{
                          marginTop: 2,
                          width: 16,
                          height: 16,
                          cursor: "pointer",
                          accentColor: "#D4620D",
                        }}
                      />
                      <div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#92400e",
                          }}
                        >
                          Mantener medidores ya registrados
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#b45309",
                            marginTop: 2,
                            lineHeight: 1.4,
                          }}
                        >
                          El nuevo auditor solo verá los medidores pendientes.
                          Los ya medidos quedan como están. Si no lo marcás, se
                          borrará todo el progreso anterior.
                        </div>
                      </div>
                    </label>
                  </div>
                );
              })()}

            {/* Valor de referencia (edit only) + valor personalizado */}
            {editingRoute &&
              (() => {
                const totalMeters = (editingRoute.meter_codes || []).reduce(
                  (sum, m) => sum + (m.meters?.length || 0),
                  0,
                );
                const empVehicle =
                  editingRoute?.employee_vehicle_type ||
                  employees.find((e) => e.id === editingRoute?.employee_id)
                    ?.vehicle_type;
                const precioPorMedidor =
                  parseFloat(tarifas?.precio_por_vehiculo?.[empVehicle]) || 0;
                if (!precioPorMedidor) return null;
                const zonaPct =
                  form.zona && tarifas?.zonas_porcentaje?.[form.zona]
                    ? parseFloat(tarifas.zonas_porcentaje[form.zona])
                    : 0;
                const precioFinal = precioPorMedidor * (1 + zonaPct / 100);
                const valorRef = Math.round(totalMeters * precioFinal);
                return (
                  <div style={m.refBox}>
                    <span style={m.refLabel}>Valor de referencia</span>
                    <span style={m.refValue}>
                      {totalMeters} medidores × $
                      {precioFinal.toLocaleString("es-AR")}
                      {zonaPct > 0 && (
                        <span style={{ color: "#92400e" }}>
                          {" "}
                          (base +{zonaPct}% Zona {form.zona})
                        </span>
                      )}
                      {" = "}
                      <strong>${valorRef.toLocaleString("es-AR")}</strong>
                    </span>
                  </div>
                );
              })()}

            <div style={m.field}>
              <label style={m.label}>
                Valor personalizado de la ruta (opcional)
              </label>
              <div style={m.inputPrefixWrap}>
                <span style={m.inputPrefixSign}>$</span>
                <input
                  style={{
                    ...m.input,
                    borderRadius: "0 8px 8px 0",
                    border: "none",
                    background: "transparent",
                  }}
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.valor_personalizado}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      valor_personalizado: e.target.value,
                    }))
                  }
                  placeholder="Dejar vacío para usar valor de referencia"
                />
              </div>
            </div>

            {error && <p style={m.error}>{error}</p>}

            <div style={m.actions}>
              <button style={m.btnCancel} onClick={closeModal}>
                Cancelar
              </button>
              <button style={m.btnSave} onClick={handleSave} disabled={saving}>
                {saving
                  ? "Guardando..."
                  : editingRoute
                    ? "Guardar cambios"
                    : "Crear ruta"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── History Modal ── */}
      {historyRoute && (
        <div
          style={m.overlay}
          onClick={(e) => e.target === e.currentTarget && setHistoryRoute(null)}
        >
          <div style={{ ...m.box, maxWidth: 520 }}>
            <div style={m.header}>
              <div>
                <h3 style={m.title}>Historial de asignaciones</h3>
                <p style={{ margin: 0, fontSize: 13, color: "var(--text-2)" }}>
                  {historyRoute.name}
                </p>
              </div>
              <button style={m.closeBtn} onClick={() => setHistoryRoute(null)}>
                <X size={16} />
              </button>
            </div>

            {/* Asignación activa actual */}
            {historyRoute.employee_name && isAdminPlus && (
              <div style={h.activeBox}>
                <div style={h.activeLeft}>
                  <CheckCircle
                    size={15}
                    color="#16a34a"
                    style={{ flexShrink: 0 }}
                  />
                  <div>
                    <div style={h.activeName}>{historyRoute.employee_name}</div>
                    <div style={h.activeLabel}>Asignación activa</div>
                  </div>
                </div>
                <button
                  style={{
                    ...h.btnComplete,
                    ...(completing ? h.btnCompleteDisabled : {}),
                  }}
                  onClick={handleCompleteAssignment}
                  disabled={completing}
                  title="Enviar a revisión"
                >
                  {completing ? "..." : "Enviar a revisión"}
                </button>
              </div>
            )}

            {historyError && <div style={h.errorBanner}>{historyError}</div>}

            {/* Lista historial */}
            <div style={h.listWrap}>
              {historyLoading ? (
                <div style={h.empty}>Cargando historial...</div>
              ) : historyData.length === 0 ? (
                <div style={h.empty}>
                  <Clock
                    size={28}
                    color="#d1d5db"
                    style={{ marginBottom: 8 }}
                  />
                  <p
                    style={{ margin: 0, color: "var(--text-2)", fontSize: 13 }}
                  >
                    No hay asignaciones anteriores
                  </p>
                </div>
              ) : (
                historyData.map((a, idx) => (
                  <div key={a.id} style={h.item}>
                    <div style={h.itemLeft}>
                      <div style={h.avatar}>
                        {a.employee_name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <div style={h.itemName}>{a.employee_name}</div>
                        <div style={h.itemEmail}>{a.employee_email}</div>
                        <div style={h.itemDates}>
                          {format(new Date(a.assigned_at), "d MMM yyyy", {
                            locale: es,
                          })}
                          {" → "}
                          {a.completed_at
                            ? format(new Date(a.completed_at), "d MMM yyyy", {
                                locale: es,
                              })
                            : "hoy"}
                        </div>
                        {a.notas && <div style={h.itemNotas}>"{a.notas}"</div>}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 6,
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          ...h.statusBadge,
                          ...(a.status === "activa"
                            ? h.statusActiva
                            : a.status === "completada"
                              ? h.statusCompletada
                              : h.statusCancelada),
                        }}
                      >
                        {a.status}
                      </span>
                      {a.status === "completada" && (
                        <button
                          style={h.verRutaBtn}
                          onClick={() => {
                            setHistoryRoute(null);
                            navigate("/gestion-ruta", {
                              state: { openAssignmentId: a.id },
                            });
                          }}
                        >
                          Ver ruta →
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  page: { display: "flex", flexDirection: "column", height: "100%" },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  title: { fontSize: 24, fontWeight: 700, color: "var(--text)", margin: 0 },
  badge: {
    background: "var(--primary-bg)",
    color: "#D4620D",
    borderRadius: 10,
    padding: "2px 10px",
    fontSize: 16,
    marginLeft: 8,
  },

  layout: {
    display: "flex",
    flex: 1,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 16px rgba(0,0,0,0.1)",
    height: "calc(100vh - 140px)",
  },

  // MAP
  mapWrap: { position: "relative", flex: "0 0 70%" },
  unroutedToggle: {
    position: "absolute",
    bottom: 24,
    left: 12,
    zIndex: 1000,
    padding: "7px 13px",
    borderRadius: 8,
    border: "1px solid var(--border)",
    background: "var(--bg-card)",
    color: "var(--text-2)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
  },
  unroutedToggleActive: {
    background: "var(--info-bg)",
    color: "var(--info-text)",
    borderColor: "var(--info-border)",
  },
  toolbar: {
    position: "absolute",
    top: 12,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "var(--bg-card)",
    borderRadius: 10,
    padding: "8px 12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
    border: "1px solid var(--border)",
  },
  toolbarHint: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    whiteSpace: "nowrap",
  },
  toolbarClear: {
    padding: "6px 12px",
    borderRadius: 7,
    border: "none",
    background: "var(--error-bg)",
    color: "var(--error-text)",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 13,
  },
  toolbarCancel: {
    padding: "6px 12px",
    borderRadius: 7,

    color: "var(--text-2)",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 13,
  },
  toolbarConfirm: {
    padding: "6px 14px",
    borderRadius: 7,
    border: "none",
    background: "#D4620D",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 13,
  },

  // SIDEBAR
  sidebar: {
    flex: "0 0 30%",
    display: "flex",
    flexDirection: "column",
    borderLeft: "1px solid var(--border)",
    height: "auto",
    overflow: "hidden",
  },
  sidebarSearch: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderBottom: "1px solid var(--border)",
    background: "var(--bg-card)",
    flexShrink: 0,
  },
  sidebarSearchInput: {
    flex: 1,
    border: "none",
    background: "transparent",
    fontSize: 13,
    color: "var(--text)",
    outline: "none",
    fontFamily: "Montserrat, sans-serif",
  },
  sidebarSearchClear: {
    background: "none",
    border: "none",
    color: "var(--text-3)",
    cursor: "pointer",
    fontSize: 12,
    padding: 2,
    lineHeight: 1,
  },
  sidebarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 18px",
    borderBottom: "1px solid var(--border)",
    flexShrink: 0,
  },
  sidebarTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  btnNew: {
    display: "inline-flex",
    alignItems: "center",
    padding: "7px 14px",
    borderRadius: 12,
    border: "none",
    background: "#D4620D",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 13,
    fontFamily: "Montserrat, sans-serif",
  },
  sidebarBody: { flex: 1, overflowY: "auto" },
  sidebarEmpty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    textAlign: "center",
    padding: 24,
  },

  // ROUTE CARDS
  routeCard: { padding: "14px 18px", borderBottom: "1px solid var(--border)" },
  routeCardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  routeCardLeft: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
  routeCardActions: { display: "flex", gap: 5, flexShrink: 0 },
  colorDot: {
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
  },
  routeName: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--text)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  routeMeta: {
    marginLeft: 18,
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  routeMetaRow: { display: "flex", alignItems: "center", gap: 5 },
  empName: { fontSize: 13, color: "#D4620D", fontWeight: 700 },
  empEmpty: { fontSize: 13, color: "var(--text-2)", fontStyle: "italic" },
  routeDate: { fontSize: 13, color: "var(--text-2)" },
  zonaBadge: {
    fontSize: 10,
    fontWeight: 700,
    background: "var(--warning-bg)",
    color: "var(--warning-text)",
    borderRadius: 5,
    padding: "2px 6px",
    marginLeft: 4,
    flexShrink: 0,
  },
  objetivoBadge: {
    fontSize: 11,
    fontWeight: 700,
    color: "#7c3aed",
    background: "#ede9fe",
    borderRadius: 6,
    padding: "3px 8px",
  },

  iconBtnGray: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 26,
    height: 26,
    borderRadius: 7,
    border: "none",
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    cursor: "pointer",
  },
  iconBtnBlue: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 26,
    height: 26,
    borderRadius: 7,
    border: "none",
    background: "var(--primary-bg)",
    color: "#D4620D",
    cursor: "pointer",
  },
  iconBtnRed: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 26,
    height: 26,
    borderRadius: 7,
    border: "none",
    background: "var(--error-bg)",
    color: "var(--error-text)",
    cursor: "pointer",
  },
};

const m = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    padding: 24,
  },
  box: {
    background: "var(--bg-card)",
    borderRadius: 16,
    padding: 28,
    width: "100%",
    maxWidth: 460,
    boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
    border: "1px solid var(--border)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 22,
    paddingBottom: 16,
    borderBottom: "1px solid var(--border)",
  },
  title: { fontSize: 17, fontWeight: 800, color: "var(--text)", margin: 0 },
  closeBtn: {
    background: "var(--bg-subtle)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    cursor: "pointer",
    color: "var(--text-2)",
    display: "flex",
    padding: 6,
  },
  editPreview: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
    padding: "10px 14px",
    background: "var(--bg-subtle)",
    borderRadius: 10,
    border: "1px solid var(--border)",
  },
  field: { marginBottom: 16 },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: "var(--text-2)",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  input: {
    width: "100%",
    padding: "10px 13px",
    borderRadius: 10,
    border: "1.5px solid var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    fontSize: 14,
    boxSizing: "border-box",
    outline: "none",
    fontFamily: "Montserrat, sans-serif",
    transition: "border-color 0.15s",
  },
  polygonOk: {
    marginBottom: 16,
    fontSize: 13,
    fontWeight: 700,
    color: "var(--success-text)",
    background: "var(--success-bg)",
    border: "1px solid var(--success-border)",
    padding: "9px 13px",
    borderRadius: 10,
  },
  polygonNone: {
    marginBottom: 16,
    fontSize: 13,
    color: "var(--text-2)",
    background: "var(--bg-subtle)",
    border: "1px solid var(--border)",
    padding: "9px 13px",
    borderRadius: 10,
  },
  refBox: {
    background: "var(--success-bg)",
    border: "1px solid var(--success-border)",
    borderRadius: 12,
    padding: "10px 14px",
    marginBottom: 16,
  },
  refLabel: {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: "var(--success-text)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: 4,
  },
  refValue: { fontSize: 13, color: "var(--text)" },
  inputPrefixWrap: {
    display: "flex",
    alignItems: "stretch",
    borderRadius: 10,
    border: "1.5px solid var(--border)",
    overflow: "hidden",
    background: "var(--bg)",
  },
  inputPrefixSign: {
    padding: "10px 12px",
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    fontSize: 14,
    fontWeight: 700,
    borderRight: "1.5px solid var(--border)",
    display: "flex",
    alignItems: "center",
  },
  error: { color: "var(--error-text)", fontSize: 13, margin: "0 0 12px" },
  actions: {
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    marginTop: 8,
    paddingTop: 16,
    borderTop: "1px solid var(--border)",
  },
  btnCancel: {
    padding: "10px 18px",
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 14,
    fontFamily: "Montserrat, sans-serif",
  },
  btnSave: {
    padding: "10px 22px",
    borderRadius: 10,
    border: "none",
    background: "#D4620D",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 14,
    fontFamily: "Montserrat, sans-serif",
  },
};

// ── History modal styles ───────────────────────────────────────────────────────
const h = {
  activeBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "var(--success-bg)",
    border: "1px solid var(--success-border)",
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: 16,
    gap: 10,
  },
  activeLeft: { display: "flex", alignItems: "center", gap: 10 },
  activeName: { fontSize: 14, fontWeight: 700, color: "var(--text)" },
  activeLabel: {
    fontSize: 11,
    color: "var(--success-text)",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  btnComplete: {
    padding: "6px 14px",
    borderRadius: 10,
    border: "none",
    background: "#16a34a",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 12,
    fontFamily: "Montserrat, sans-serif",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  btnCompleteDisabled: { opacity: 0.6, cursor: "not-allowed" },

  errorBanner: {
    background: "var(--error-bg)",
    color: "var(--error-text)",
    borderRadius: 10,
    padding: "9px 14px",
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 12,
  },

  listWrap: {
    maxHeight: 380,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "32px 0",
    color: "var(--text-2)",
    fontSize: 13,
  },

  item: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "12px 4px",
    borderBottom: "1px solid var(--border)",
    gap: 10,
  },
  itemLeft: { display: "flex", alignItems: "flex-start", gap: 12 },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "var(--info-bg)",
    color: "var(--info-text)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 14,
    flexShrink: 0,
  },
  itemName: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--text)",
    marginBottom: 2,
  },
  itemEmail: { fontSize: 11, color: "var(--text-2)", marginBottom: 3 },
  itemDates: { fontSize: 12, color: "var(--text-2)" },
  itemNotas: {
    fontSize: 12,
    color: "var(--text-2)",
    fontStyle: "italic",
    marginTop: 3,
  },

  statusBadge: {
    fontSize: 10,
    fontWeight: 700,
    borderRadius: 6,
    padding: "3px 8px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    flexShrink: 0,
    alignSelf: "center",
  },
  statusActiva: {
    background: "var(--success-bg)",
    color: "var(--success-text)",
  },
  statusCompletada: { background: "var(--bg-subtle)", color: "var(--text-2)" },
  statusCancelada: {
    background: "var(--error-bg)",
    color: "var(--error-text)",
  },
  verRutaBtn: {
    background: "none",
    border: "1px solid var(--border)",
    borderRadius: 7,
    padding: "3px 10px",
    fontSize: 12,
    fontWeight: 700,
    color: "#D4620D",
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
    whiteSpace: "nowrap",
  },
};
